/*
 * Build My Stak card behaviour.
 *
 * Sits alongside assets/custom-events.js and assets/theme.js rather than inside
 * them, so the shared grid and cart scripts keep working unchanged everywhere
 * else. Jobs:
 *
 *   1. The photo carousel's view dots (swiping itself is native — see below).
 *   2. Relabelling the CTA to "Add to stak" once a shade is chosen.
 *   3. Holding the add-to-cart until a shade has actually been picked.
 *   4. Accessibility wiring the shared swatch snippet cannot do on its own.
 *
 * The carousel is a native scroll-snap container, not a transformed track:
 * something in the theme strips `transform` off .product-item__media at
 * runtime — it is undone even when set !important — so a transformed track
 * never moved. Scrolling also gives touch swiping, momentum and keyboard
 * arrow-key support for free.
 *
 * On ordering: bindGridEvents() in custom-events.js binds `change` directly to
 * each swatch input and sets the label to "Add To Bag"; theme.js binds `click`
 * directly to .product-item__submit. Both bind before this deferred script
 * runs. So the relabel listens on `document` during the bubble phase (after
 * every handler on the target), while the add-to-cart guard listens during the
 * CAPTURE phase (before them), which is the only way to stop the cart call.
 */
(function () {
  'use strict';

  var CARD = '.build-stak-card';

  /* The "+N" is derived from what CSS actually hides, not from a number baked
     in at render time. The visible count changes with the breakpoint, so a
     fixed Liquid figure would disagree with the row — and when it disagreed
     downward, shades sat hidden with no control to reveal them. */
  function syncOverflowNote(card) {
    var list = card.querySelector('.product-swatches-options__list');
    var note = card.querySelector('.product-swatches-options__item-overflow-note');
    if (!list || !note) return;

    var items = list.querySelectorAll('li:not(.product-swatches-options__item-overflow-note)');
    var hidden = 0;
    for (var i = 0; i < items.length; i++) {
      if (getComputedStyle(items[i]).display === 'none') hidden++;
    }

    if (hidden === 0) {
      note.style.display = 'none';
      return;
    }

    note.style.display = '';
    var amount = note.querySelector('.overflow_amount');
    if (amount) amount.textContent = '+' + hidden;
    note.setAttribute('aria-label', 'Show ' + hidden + ' more ' + (hidden === 1 ? 'shade' : 'shades'));
  }

  function syncAllOverflowNotes() {
    var cards = document.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) syncOverflowNote(cards[i]);
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncAllOverflowNotes, 150);
  });

  /* ---------------------------------------------------------------- slides */

  /* The dots jump; they do not glide. Every eased path was either swallowed or
     unsafe here: scrollTo({behavior:'smooth'}) does nothing, a scrollLeft
     assignment under `scroll-behavior: smooth` does nothing, and driving frames
     with requestAnimationFrame stalls whenever the tab is backgrounded — which
     would strand `scroll-snap-type` at none and leave the carousel unsnapped.

     A plain assignment always lands, and swiping still glides, because that
     motion is the browser's own scrolling rather than anything scripted. */

  function slideCount(card) {
    return parseInt(card.getAttribute('data-slide-count'), 10) || 1;
  }

  function markDots(card, index) {
    card.setAttribute('data-slide', String(index));
    var dots = card.querySelectorAll('[data-build-stak-dot]');
    for (var i = 0; i < dots.length; i++) {
      dots[i].setAttribute('aria-pressed', i === index ? 'true' : 'false');
    }
  }

  function goToSlide(card, index) {
    var track = card.querySelector('[data-build-stak-track]');
    if (!track) return;

    var count = slideCount(card);
    if (index < 0) index = 0;
    if (index > count - 1) index = count - 1;

    track.scrollLeft = index * track.clientWidth;
    markDots(card, index);
  }

  document.addEventListener('click', function (evt) {
    var dot = evt.target.closest && evt.target.closest('[data-build-stak-dot]');
    if (!dot) return;
    var card = dot.closest(CARD);
    if (!card) return;
    evt.preventDefault();
    goToSlide(card, parseInt(dot.getAttribute('data-build-stak-dot'), 10) || 0);
  });

  // Keep the dots honest when the shopper swipes or arrow-keys the track
  // instead of using them.
  function watchScroll(card) {
    var track = card.querySelector('[data-build-stak-track]');
    if (!track || slideCount(card) < 2) return;

    var timer = null;
    track.addEventListener('scroll', function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        var w = track.clientWidth || 1;
        markDots(card, Math.round(track.scrollLeft / w));
      }, 80);
    }, { passive: true });
  }

  /* ------------------------------------------------------------- CTA state */

  function shadeChosen(card) {
    var hidden = card.querySelector('.product-swatches-options [type="hidden"]');
    if (hidden && hidden.value) return true;
    // Single-shade products carry their id directly on the card instead.
    var direct = card.querySelector('.build-stak-card__shades > input[name="id"]');
    return !!(direct && direct.value);
  }

  function statusLine(card) {
    return card.querySelector('.product-swatches-options__selected');
  }

  function relabel(card) {
    var label = card.querySelector('button[name="add"] .button-text');
    var wanted = card.getAttribute('data-add-label');
    if (!label || !wanted) return;

    // Only rename the plain add state. Sold Out / Pre-Order / Coming Soon are
    // real status the shared script sets, and must not be overwritten.
    if (label.textContent.trim().toLowerCase() === 'add to bag') {
      label.textContent = wanted;
    }
  }

  // Capture phase: this has to run before theme.js's own click handler on
  // .product-item__submit, which would otherwise post a form with no variant.
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('.build-stak-card__cta');
    if (!btn || btn.getAttribute('name') !== 'add') return;

    var card = btn.closest(CARD);
    if (!card || shadeChosen(card)) return;

    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();

    var status = statusLine(card);
    if (status) status.textContent = 'Pick a shade first';

    // Nudge the row rather than focusing the first swatch: a focus ring on that
    // swatch reads as though it had been selected, which is the opposite of the
    // message. The status line is a live region, so a screen reader still hears
    // "Pick a shade first" without focus having to move.
    var list = card.querySelector('.product-swatches-options__list');
    if (list) {
      list.classList.remove('build-stak-card__shades--nudge');
      void list.offsetWidth; // reflow, so the animation restarts on repeat taps
      list.classList.add('build-stak-card__shades--nudge');
    }
  }, true);

  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || input.name !== 'option-0') return;

    var card = input.closest && input.closest(CARD);
    if (!card) return;

    // A new shade means a new photo in slide 1 — show it rather than leaving
    // the shopper on whichever view they had scrolled to.
    swapThirdSlide(card, input);
    goToSlide(card, 0);
    relabel(card);
  });

  /* custom-events.js rewrites slides one and two from data-image-one/-two when a
     shade changes. It knows nothing about a third, so the third view stayed on
     whatever the page rendered — a generic product photo rather than one of the
     chosen shade's. product-item-swatches now carries data-image-three, and
     this puts it on the slide. */
  function swapThirdSlide(card, input) {
    var src = input.getAttribute('data-image-three');
    var slides = card.querySelectorAll('.build-stak-card__slide img');
    if (slides.length < 3) return;
    var img = slides[2];
    if (!src) return;
    img.setAttribute('src', src);
    img.setAttribute('alt', input.value);
    img.removeAttribute('srcset');
  }

  /* ------------------------------------------------------- a11y of the card */

  function enhance(card) {
    // The selected-shade line changes without a page load, so it needs to be a
    // live region or the change is silent to a screen reader.
    var status = statusLine(card);
    if (status && !status.getAttribute('role')) {
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
    }

    // The shared snippet renders "+N" as a tabbable <li>. Give it the role its
    // behaviour already implies so it is announced as a button, not list text.
    var note = card.querySelector('.product-swatches-options__item-overflow-note');
    if (note && !note.getAttribute('role')) {
      note.setAttribute('role', 'button');
      note.setAttribute('aria-expanded', 'false');
      note.addEventListener('click', function () {
        note.setAttribute('aria-expanded', 'true');
        note.style.display = 'none';
      });
    }

    // A New / Sale pill sits beside its swatch as loose text. Fold it into the
    // radio's own accessible name and hide the duplicate, so the shade is
    // announced as "Petal to the Metal, New" rather than as two stray strings —
    // and so the badge is not conveyed by colour and position alone.
    var items = card.querySelectorAll('.product-swatches-options__list li');
    for (var i = 0; i < items.length; i++) {
      var pill = items[i].querySelector('.product-badge');
      var radio = items[i].querySelector('input[name="option-0"]');
      var label = items[i].querySelector('label');
      if (!pill || !radio || !label) continue;
      if (pill.getAttribute('aria-hidden') === 'true') continue;
      pill.setAttribute('aria-hidden', 'true');
      radio.setAttribute('aria-label', label.textContent.trim() + ', ' + pill.textContent.trim());
    }
  }

  /* ------------------------------------------------------------------ init */

  // A single-shade product has its swatch clicked by bindGridEvents the moment
  // custom-events.js runs, which is before this deferred script exists — so its
  // button is already relabelled to "Add To Bag" and no change event is coming.
  // Sweep once on load to catch those.
  function init() {
    var cards = document.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) {
      relabel(cards[i]);
      enhance(cards[i]);
      watchScroll(cards[i]);
      syncOverflowNote(cards[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
